import { MetaApiResponse } from './meta.api-response';

export interface CatalogApiResponse extends MetaApiResponse {
    outlet: Outlet;
    nearby: any[];
    outside_link: OutsideLink;
}

export type OutletStatus = {
    heading: string;
    message: string;
    icon: string;
    allow_refresh: boolean;
    outlet_state: OutletState;
};

export interface Outlet {
    id: number;
    code: string;
    name: string;
    active: boolean;
    address_id: string;
    agent_id: string;
    dflt: boolean;
    preorder_available: boolean;
    type_label: string;
    type_desc: string;
    service_type: string;
    oa: boolean;
    fetch: boolean;
    open: boolean;
    delivery_charges: DeliveryCharges;
    outlet_timings: OutletTiming[];
    opens_at: string;
    menu_id: number;
    interval: number;
    service_area_id: number;
    brand_id: number;
    brand_name: string;
    membership_layout: string;
    multi_brand_order_allowed: boolean;
    membership?: Membership; //app brand
    memberships?: Membership[];
    menu_items: MenuItem[];
    banners: Banners;
    announcement: null;
    product_sizes: ProductSize[];
    products: Product[];
    product_categories: ProductCategory[];
    product_customizations: ProductCustomization[];
    product_customization_types: ProductCustomizationType[];
    passes: Pass[];
    layout: number[];
    outlet_service_types: OutletServiceType[];
    take_away: boolean;
    max_item_quantity: number;
    max_total_quantity: number;
    freebie_details: FreebieDetails;
    ct_groups: any[];
    membership_actions: { [key: string]: Action };
    category_actions: CategoryActions;
    product_t_config: ProductTConfig;
    distinct_menus: boolean;
    sampling_details: SamplingDetails;
    outlet_status_strip: OutletStatus;
    outlet_state: OutletState;
}

export interface Banners {
    promotional_leaderboard: Banner[];
    icon_banners: Banner[];
    main_promotional_card: Banner[];
}

export interface Banner {
    id: number;
    name: string;
    banner_type: BannerType;
    supported_app_ids: string[];
    banner_url: string;
    action_url: null | string;
    action_type: string;
    action_title: string;
    platforms: Platform[];
    override_action: Action | null;
    tracking_id: string; // to ask
}

export enum BannerType {
    IconBanners = 'icon_banners',
    MainPromotionalCard = 'main_promotional_card',
    PromotionalLeaderboard = 'promotional_leaderboard'
}

export interface Action {
    at: ActionType;
    al: string;
    br: number;
}

export enum ActionType {
    CategoryLink = 'category_link',
    DeepLink = 'deep_link',
    ProductLink = 'product_link'
}

export enum Platform {
    Android = 'android',
    Ios = 'ios',
    Web = 'web'
}

export interface CategoryActions {}

export interface DeliveryCharges {
    minimum_delivery_charge: number;
    preorder_minimum_delivery_charge: number;
}

export interface FreebieDetails {
    min_cart_amount: number;
    applicable_for_outlet: boolean;
    texts: Texts;
}

export interface Texts {
    success: string;
    failure: string;
}

export interface Membership {
    id: number;
    name: string;
    label: string;
    tagline: string;
    code: string;
    active: number;
    brand_tag_list: any[];
    brand_tagline: string;
    seq: number;
    category_layout: string;
    product_card_layout: string;
    oft: string;
    lottie: string; // app brand
    override_action: Action | null; // custom
    category: ProductCategory[]; // custom
}

export interface MenuItem {
    pid: number;
    ptype: Ptype;
    prices: Prices;
    item_type: ItemType;
    sold_out: number;
}

export enum ItemType {
    Fi = 'fi',
    Mi = 'mi'
}

export interface Prices {
    price: number;
    discounted_price: number;
    subscription_prices: SubscriptionPrice[];
}

export interface SubscriptionPrice {
    pass_id: number;
    price: number;
}

export enum Ptype {
    PC = 'pc',
    PS = 'ps'
}

export interface OutletServiceType {
    id: number;
    name: string;
    code: string;
}

export interface OutletTiming {
    sob_time: Date;
    cob_time: Date;
}

export interface Pass {
    id: number;
    name: string;
    expiry: number;
    order_count: number;
    trial: number;
    dflt: number;
    description: string;
    product_id: number;
    product_size_id: number;
}

export interface ProductCategory {
    id: number;
    name: string;
    code: string;
    label: string;
    active: number;
    saleable: number;
    path: string;
    max_qty: null;
    shape: string;
    is_subscribable: number;
    brand_id: number;
    description: string;
    sequence: number;
    override_action: Action | null;
    image: string; // custom
}

export interface ProductCustomizationType {
    id: number;
    label: string;
    min: number;
    max: number;
    intrinsic: number;
    pack_flag: number;
    show_images: number;
    filter_opts: any[];
    ct_group_id: null;
    description: string;
    sequence: number;
    product_size_ids: number[];
    sold_out: boolean;
    customizations: ProductCustomization[];
}

export interface ProductCustomization {
    id: number;
    dflt: number;
    label: string;
    veg: number;
    description: string;
    sequence: number;
    sold_out: boolean;
    product_customization_type_id: number;
}

export interface ProductSize {
    product_customization_types: [];
    id: number;
    dflt: number;
    label: string | null;
    description: string;
    sequence: number | null;
    product_id: number;
    is_subscription_item: number;
}

export interface ProductTConfig {
    bg: Bg[];
}

export interface Bg {
    seq: number;
    d_val: number;
    d_type: string;
    d_text: string;
}

export interface Product {
    id: number;
    label: string;
    veg: number;
    upsell: number | null;
    max_qty: number | null;
    subcategory_name: null | string;
    tag_list: string[];
    t: null;
    oft: null;
    description: string;
    sequence: number | null;
    category_id: number;
    grouped_ct: number;
    is_subscription_item: number;
}

export interface SamplingDetails {
    popup_header: string;
    popup_body: string;
    popup_footer: string;
    override_texts: OverrideTexts;
    multi_item_strip_text: string;
    items: any[];
    x_p_ids: null[];
    ex_p_ids: { [key: string]: number[] };
    ex_b_ids: number[];
}

export interface OverrideTexts {
    popup_footer: PopupFooter;
}

export interface PopupFooter {
    text: string;
    threshold: number;
}

export interface OutsideLink {
    outlet_id: number;
    outlet_name: string;
    type_label: string;
    type_desc: string;
    serviceable: boolean;
    agent_id: null;
    service_type: string;
    oa: boolean;
    fetch: boolean;
}

export enum OutletState {
    PREORDER = 'pre-order',
    CLOSED = 'closed',
    ACTIVE = 'active',
    PAUSED = 'paused',
    OUT_OF_REACH = 'out-of-reach'
}
